import { z } from 'zod';
import { caseIdSchema, epochMsSchema, requestIdSchema, roleSchema, surrogateKeySchema, } from './primitives.js';
import { actionKindSchema, clerkEventSchema, markEventSchema } from './status.js';
import { patientDisplaySchema } from './patient.js';
/** 内線番号。縮退時・調製開始後の変更／中止で表示する。 */
export const extensionSchema = z.object({ label: z.string().max(32), no: z.string().max(16) });
export const settingsSchema = z.object({
    /** 滞留アラート：確定からこの分数、調製開始されなければ強調 */
    alertMin: z.number().int().min(1).max(180),
    ext: z.array(extensionSchema).max(12),
    /**
     * 誤タップ取消を認める時間（分）。要件6-4。
     * 上限は1日。既定の480分は確定済みHTMLの挙動に合わせた値で、
     * G1で「数分」に絞る場合は設定値を下げる（引き継ぎ資料 3章 #3）。
     * 上限を60分にすると既定値を自分の契約で拒否してしまうため、ここは広く取る。
     */
    undoWindowMin: z
        .number()
        .int()
        .min(1)
        .max(24 * 60),
});
/**
 * 1患者・1日分の案件。業務状態（永続）と患者表示情報（都度解決）を分けて返す。
 * patient が null のときは院内参照が取れていない＝縮退表示。
 */
export const caseViewSchema = z.object({
    caseId: caseIdSchema,
    surrogateKey: surrogateKeySchema,
    /** 名簿の並び順。入力前一覧の並びに使う。 */
    rosterIndex: z.number().int().nonnegative(),
    rpCount: z.number().int().min(1).max(9),
    /** 楽観ロック用。更新時に expectedVersion として送り返す。 */
    version: z.number().int().nonnegative(),
    clerk: clerkEventSchema.nullable(),
    pharm: markEventSchema.nullable(),
    admin: markEventSchema.nullable(),
    stopAck: markEventSchema.nullable(),
    /** 調製完了（薬剤部での調製が終わった）。調製開始の後にだけ入る。 */
    pharmDone: markEventSchema.nullable(),
    /** 投与完了。投与開始の後にだけ入る。 */
    adminDone: markEventSchema.nullable(),
    /** 直前の取消で「調製が始まっていた」痕跡。電話併用の案内に使う。 */
    wasStarted: z.boolean(),
    patient: patientDisplaySchema.nullable(),
});
export const boardResponseSchema = z.object({
    serverTime: epochMsSchema,
    updatedAt: epochMsSchema,
    /** 患者表示情報を解決できなくなった時刻。null なら正常。 */
    degradedSince: epochMsSchema.nullable(),
    settings: settingsSchema,
    cases: z.array(caseViewSchema),
});
export const actionRequestSchema = z.object({
    requestId: requestIdSchema,
    action: actionKindSchema,
    reason: z.string().max(500).optional(),
    expectedVersion: z.number().int().nonnegative(),
});
export const undoRequestSchema = z.object({
    requestId: requestIdSchema,
    expectedVersion: z.number().int().nonnegative(),
});
export const actionResponseSchema = z.object({
    case: caseViewSchema,
    serverTime: epochMsSchema,
    /** 調製開始後の変更・中止など、電話併用を促す必要がある場合に入る。 */
    callRequired: z.object({ reason: z.string(), extension: extensionSchema.nullable() }).nullable(),
});
/** ログインしている人。ロールは院内Entraのロール割当に対応させる。 */
export const sessionUserSchema = z.object({
    actorId: z.string().min(1).max(64),
    displayName: z.string().max(64),
    role: roleSchema,
});
/**
 * 操作ログ。患者氏名・患者IDを持たない（要件 #10）。
 * 画面表示時に代理キーから氏名を都度解決する。
 */
export const auditEntrySchema = z.object({
    at: epochMsSchema,
    actorId: z.string().max(64),
    actorName: z.string().max(64),
    actorRole: roleSchema,
    action: z.string().max(64),
    surrogateKey: surrogateKeySchema.nullable(),
    result: z.enum(['ok', 'conflict', 'denied', 'error']),
});
export const auditResponseSchema = z.object({
    entries: z.array(auditEntrySchema),
    /** 代理キー → 患者表示情報。ログ本体には保存されず、都度解決したもの。 */
    patients: z.record(z.string(), patientDisplaySchema),
});
export const apiErrorSchema = z.object({
    error: z.object({
        code: z.enum([
            'bad_request',
            'unauthorized',
            'forbidden',
            'not_found',
            'version_conflict',
            'transition_denied',
            'undo_expired',
            'upstream_unavailable',
            'internal',
        ]),
        message: z.string(),
        /** 競合時に最新状態を返し、クライアントが再送しないで済むようにする。 */
        current: caseViewSchema.optional(),
    }),
});
export const rosterImportRowSchema = z.object({
    patientId: z.string().min(1).max(32),
    name: z.string().min(1).max(64),
    dept: z.string().max(64).default(''),
    age: z.number().int().min(0).max(120).nullable().default(null),
    sex: z.string().max(8).default(''),
    birth: z.string().max(16).default(''),
    rpCount: z.number().int().min(1).max(9).default(1),
});
export const rosterImportRequestSchema = z.object({
    requestId: requestIdSchema,
    mode: z.enum(['replace', 'append']),
    rows: z.array(rosterImportRowSchema).min(1).max(500),
});
/**
 * いま配っている画面の版。入口のJSのパス（内容のハッシュ入り。例 /assets/index-XXXX.js）をそのまま使う。
 * すべての /api 応答に付き、画面は自分が動かしている版と比べて、配備後の古い画面を自動で読み込み直す。
 */
export const WEB_VERSION_HEADER = 'x-chemoflow-web';
//# sourceMappingURL=api.js.map